import { contactLine, FlowTemplate, leadIcon, sectionIcon, singleColumnPage, type RenderContext, type Zone } from "../base";
import { center, col, expanded, hrule, keep, linkText, padding, rich, row, text, vspace, type Node, type PageSpec } from "../ir";
import { hasCategories, skillGroups, type RenderEntry, type RenderSection } from "../model";
import type { TextStyle } from "../theme";

/**
 * Executive — a timeless centred serif layout with ruled headings, the format
 * recruiters at banks, law firms and consultancies expect.
 * (executive_template.dart)
 */
export class ExecutiveTemplate extends FlowTemplate {
  readonly info = {
    id: "executive",
    name: "Executive",
    tagline: "Timeless centred serif for senior roles",
    category: "classic" as const,
    layout: "One column",
    defaultAccent: 0xff1f3a5f,
    atsFriendly: true,
    supportsPhoto: false,
  };

  readonly fonts = { body: "crimson" as const };

  zoneWidth(c: RenderContext): number {
    return c.format.width - 96;
  }

  sectionGap(c: RenderContext): number {
    return c.t.gap(13);
  }

  paragraphStyle(c: RenderContext): TextStyle {
    return c.t.style(c.t.body.regular, 9.6, { color: c.t.text });
  }

  bulletGlyph(c: RenderContext): Node {
    return text("•", c.t.style(c.t.body.regular, 9.6, { color: c.t.ink, lineSpacing: 0 }));
  }

  heading(c: RenderContext, s: RenderSection): Node {
    const t = c.t;
    return col(
      [
        center(
          leadIcon(
            sectionIcon(c, s, t.accentText, t.fs(11.5)),
            text(s.title.toUpperCase(), t.style(t.heading.semiBold, 10.4, { color: t.accentText, letterSpacing: 2.4, lineSpacing: 0 })),
            { gap: 7, center: true }
          )
        ),
        vspace(3),
        hrule(0.6, t.ink),
      ],
      { align: "center" }
    );
  }

  entryHeader(c: RenderContext, e: RenderEntry): Node {
    const t = c.t;
    const strong = t.style(t.body.bold, 10, { color: t.ink, lineSpacing: 0 });
    const italic = t.style(t.body.italic, 9.8, { color: t.text, lineSpacing: 0 });
    const right = t.style(t.body.regular, 9.2, { color: t.text, lineSpacing: 0 });
    const rightItalic = t.style(t.body.italic, 9.2, { color: t.muted, lineSpacing: 0 });
    // Organisation first (bold) with location; role in italics with dates.
    const org = e.subtitle || e.title;
    const role = e.subtitle ? e.title : "";
    return col([
      row([expanded(linkText(org, strong, e.url)), e.meta && text(e.meta, right)], { align: "start" }),
      (role || e.dates) &&
        padding({ top: 1.5 }, row([expanded(text(role, italic)), e.dates && text(e.dates, rightItalic)])),
    ]);
  }

  techTags(c: RenderContext, tags: string[]): Node {
    return text(`Technologies: ${tags.join(", ")}`, c.t.style(c.t.body.italic, 9.2, { color: c.t.muted }));
  }

  skillsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (c.showSkillLevels) return this.levelList(c, s.skills, z, { grouped: hasCategories(s), dots: true });
    const t = c.t;
    return skillGroups(s).map(([key, list]) =>
      padding(
        { bottom: this.lineGap(c, z) + 1 },
        rich([
          key ? [`${key}: `, t.style(t.body.bold, 9.6, { color: t.ink })] : null,
          [list.map((x) => x.name).join("  ·  "), t.style(t.body.regular, 9.6, { color: t.text })],
        ])
      )
    );
  }

  languagesContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (c.showSkillLevels) return this.levelList(c, s.skills, z, { dots: true });
    const t = c.t;
    return [
      text(
        s.skills.map((l) => `${l.name} (${l.levelLabel})`).join("  ·  "),
        t.style(t.body.regular, 9.6, { color: t.text })
      ),
    ];
  }

  tagsContent(c: RenderContext, s: RenderSection): Node[] {
    return [text(s.tags.join("  ·  "), c.t.style(c.t.body.regular, 9.6, { color: c.t.text }))];
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const header = col(
      [
        m.name &&
          text(m.name.toUpperCase(), t.style(t.display.semiBold, 23, { color: t.ink, letterSpacing: 3, lineSpacing: 0 }), "center"),
        m.headline && vspace(4),
        m.headline && text(m.headline, t.style(t.body.italic, 11.5, { color: t.muted, lineSpacing: 0 }), "center"),
        m.contacts.length > 0 && vspace(7),
        m.contacts.length > 0 && contactLine(c, { style: t.style(t.body.regular, 9.2, { color: t.text, lineSpacing: 0 }) }),
        vspace(10),
        hrule(1.4, t.ink),
        vspace(1.6),
        hrule(0.5, t.ink),
      ],
      { align: "center" }
    );
    return singleColumnPage(c, [48, 42, 48, 32], [keep([header, vspace(t.gap(16))], true), ...this.allSections(c, m.sections)]);
  }
}
